import React, { useState, useEffect } from 'react';
import s from './s.module.less';
import OnboardingStepContainer from '../OnboardingStepContainer';
import CheckboxOption from '../CheckboxOption';
import ActionButtons from '../ActionButtons';
import { Input, Spin, message } from 'antd';
import { getSpecialtyList } from 'api/ppaLeadForm';

interface ServiceSelectionStepProps {
    onServicesChange?: (services: string[]) => void;
    onBack?: () => void;
    onNext?: () => void;
    currentStep?: number;
    totalSteps?: number;
    initialServices?: string[];
    initialOthersText?: string;
}

// 定义专业领域数据类型
interface SpecialtyOption {
    id: string;
    label: string;
}

export default function ServiceSelectionStep({
    onServicesChange,
    onBack,
    onNext,
    currentStep = 2,
    totalSteps = 7,
    initialServices = [],
    initialOthersText = '',
}: ServiceSelectionStepProps) {
    // 状态管理
    const [specialtyOptions, setSpecialtyOptions] = useState<SpecialtyOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // 获取专业领域数据
    useEffect(() => {
        const fetchSpecialtyList = async () => {
            try {
                setLoading(true);
                setError(null);
                const { data } = await getSpecialtyList();
                // eslint-disable-next-line no-console
                console.log('response: ', data);

                if (data && data.code === 200) {
                    // 假设API返回的数据格式为 { data: [{ id: string, name: string }] }
                    const specialties =
                        data.data?.map(
                            (item: {
                                id?: string;
                                specialtyName?: string;
                            }) => ({
                                id: item.specialtyName,
                                label: item.specialtyName || '',
                            }),
                        ) || [];

                    // 添加"Others"选项
                    specialties.push({ id: 'others', label: 'Others' });

                    setSpecialtyOptions(specialties);
                } else {
                    throw new Error(
                        data?.message || 'Failed to fetch specialty list',
                    );
                }
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('Error fetching specialty list:', err);
                setError(err instanceof Error ? err.message : 'Failed to load specialty options');
                message.error('Failed to load specialty options. Please try again.');
                setSpecialtyOptions([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSpecialtyList();
    }, []);

    const [selectedServices, setSelectedServices] = useState<string[]>([]);
    const [othersText, setOthersText] = useState<string>('');

    // 处理初始数据回显逻辑 - 在specialtyOptions加载完成后执行
    useEffect(() => {
        if (specialtyOptions.length === 0 || initialServices.length === 0) {
            return;
        }

        const processInitialData = () => {
            const processedServices: string[] = [];
            const customServices: string[] = [];
            let hasOthers = false;

            const predefinedServiceIds = specialtyOptions.map((option) => option.id);

            initialServices.forEach((service) => {
                if (predefinedServiceIds.includes(service)) {
                    // 如果是预定义的服务选项，直接添加
                    processedServices.push(service);
                } else {
                    // 如果不是预定义的选项，说明是others的自定义内容
                    customServices.push(service);
                    hasOthers = true;
                }
            });

            // 如果有自定义内容，添加others选项
            if (hasOthers) {
                processedServices.push('others');
            }

            // 将多个自定义内容用逗号分隔合并（如果有多个的话）
            const processedOthersText = customServices.length > 0
                ? customServices.join(', ')
                : initialOthersText;

            return {
                services: Array.from(new Set(processedServices)), // 去重，确保others只出现一次
                othersText: processedOthersText,
            };
        };

        const { services: processedServices, othersText: processedOthersText } = processInitialData();

        setSelectedServices(processedServices);
        setOthersText(processedOthersText);
    }, [specialtyOptions, initialServices, initialOthersText]);

    const handleServiceChange = (serviceId: string, checked: boolean) => {
        let newSelectedServices: string[];

        if (checked) {
            newSelectedServices = [...selectedServices, serviceId];
        } else {
            newSelectedServices = selectedServices.filter((id) => id !== serviceId);
            // 如果取消选择 "others"，清空输入文本
            if (serviceId === 'others') {
                setOthersText('');
            }
        }

        setSelectedServices(newSelectedServices);
    };

    const handleOthersTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newText = event.target.value;
        setOthersText(newText);
    };

    // 验证表单是否可以提交
    const isFormValid = () => {
        if (selectedServices.length === 0) {
            return false;
        }

        // 如果选择了others，必须填写内容
        if (selectedServices.includes('others') && !othersText.trim()) {
            return false;
        }

        return true;
    };

    const handleNext = () => {
        if (!isFormValid()) {
            return;
        }

        // 处理服务数据：如果选择了others，用input的值替换"others"
        const finalServices = selectedServices.map((service) => {
            if (service === 'others' && othersText.trim()) {
                return othersText.trim();
            }
            return service;
        });

        // 调用父组件的回调函数
        if (onServicesChange) {
            onServicesChange(finalServices);
        }

        // 调用原本的onNext函数
        if (onNext) {
            onNext();
        }
    };

    const renderServiceRows = () => {
        if (loading) {
            return (
                <div className={s.loadingContainer}>
                    <Spin size="large" />
                    <p>Loading specialty options...</p>
                </div>
            );
        }

        if (error) {
            return (
                <div className={s.errorContainer}>
                    <p>Failed to load specialty options. Using default options.</p>
                </div>
            );
        }

        return (
            specialtyOptions.map((service) => (
                <CheckboxOption
                    key={service.id}
                    id={service.id}
                    label={service.label}
                    checked={selectedServices.includes(service.id)}
                    onChange={handleServiceChange}
                    width={
                        {
                            pc: 268,
                            mobile: '100%',
                        }
                    }
                />
            ))
        );
    };

    return (
        <>
            <OnboardingStepContainer
                currentStep={currentStep}
                totalSteps={totalSteps}
                title="Which services does your practice offer?"
                subtitle="Select all that apply — this will help us match you with the right patients."
                showBackButton
                onBack={onBack}
            >
                <div className={s.servicesContainer}>
                    {renderServiceRows()}

                    {/* Others 输入框 - 当选择了 others 时显示 */}
                    {selectedServices.includes('others') && (
                        <div className={s.othersInputContainer}>
                            <label htmlFor="others-input" className={s.othersLabel}>
                                Please specify
                            </label>
                            <Input
                                id="others-input"
                                type="text"
                                value={othersText}
                                onChange={handleOthersTextChange}
                                placeholder="List any other services you're offering..."
                                className={s.othersInput}
                            />
                        </div>
                    )}
                </div>
            </OnboardingStepContainer>

            <ActionButtons
                showBack
                onNext={handleNext}
                nextDisabled={!isFormValid()}
                currentStep={currentStep}
                onBack={onBack}
            />
        </>
    );
}
